// src/components/commercials/CommercialForm.js
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Alert,
  Paper,
  Divider,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from "@mui/material";
import { Close, Save, Add, Delete } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import { CONTACT_TYPES } from "../../utils/models/contactModel";
import { createCompany } from "../../utils/models/companyModel";

const CommercialForm = ({ commercial, onSave, onCancel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { add: addCommercial, update: updateCommercial } =
    useAuditFirestore("commercials");
  const { add: addContact, update: updateContact } =
    useAuditFirestore("contacts");
  const { add: addCompany } = useAuditFirestore("companies");
  const { data: users } = useCollection("users");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    firstName: commercial?.firstName || "",
    lastName: commercial?.lastName || "",
    dni: commercial?.dni || "",
    email: commercial?.email || "",
    phone: commercial?.phone || "",
    userId: commercial?.userId || "",
    companyName: commercial?.companyName || "",
    companyTaxId: commercial?.companyTaxId || "",
    companyAddress: {
      street: commercial?.companyAddress?.street || "",
      city: commercial?.companyAddress?.city || "",
      state: commercial?.companyAddress?.state || "",
      zipCode: commercial?.companyAddress?.zipCode || "",
      country: commercial?.companyAddress?.country || "España",
    },
    percentage: commercial?.percentage || "",
    contactId: commercial?.contactId || "",
    companyId: commercial?.companyId || "",
    files: commercial?.files || [],
    notes: commercial?.notes || "",
    isActive: commercial?.isActive !== false,
  });

  useEffect(() => {
    setFormData({
      firstName: commercial?.firstName || "",
      lastName: commercial?.lastName || "",
      dni: commercial?.dni || "",
      email: commercial?.email || "",
      phone: commercial?.phone || "",
      userId: commercial?.userId || "",
      companyName: commercial?.companyName || "",
      companyTaxId: commercial?.companyTaxId || "",
      companyAddress: {
        street: commercial?.companyAddress?.street || "",
        city: commercial?.companyAddress?.city || "",
        state: commercial?.companyAddress?.state || "",
        zipCode: commercial?.companyAddress?.zipCode || "",
        country: commercial?.companyAddress?.country || "España",
      },
      percentage: commercial?.percentage || "",
      contactId: commercial?.contactId || "",
      companyId: commercial?.companyId || "",
      files: commercial?.files || [],
      notes: commercial?.notes || "",
      isActive: commercial?.isActive !== false,
    });
  }, [commercial]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("companyAddress.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        companyAddress: { ...prev.companyAddress, [field]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (error) setError("");
  };

  // Manejar archivos
  const handleFileChange = (index, field, value) => {
    const newFiles = [...formData.files];
    newFiles[index] = { ...newFiles[index], [field]: value };
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleAddFile = () => {
    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, { name: "", url: "" }],
    }));
  };

  const handleRemoveFile = (index) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName) {
      setError("Nombre y apellidos son obligatorios");
      return;
    }

    // Filtrar archivos vacíos
    const validFiles = formData.files.filter(
      (file) => file.name.trim() !== "" && file.url.trim() !== "",
    );

    setLoading(true);
    setError("");

    try {
      let contactId = formData.contactId;
      let companyId = formData.companyId;

      // 1. Crear empresa si no existe y hay nombre de empresa
      if (!companyId && formData.companyName) {
        const companyData = createCompany({
          name: formData.companyName,
          taxId: formData.companyTaxId || "",
          email: formData.email || "",
          phone: formData.phone || "",
          address: formData.companyAddress,
          notes: `Empresa creada desde comercial: ${formData.firstName} ${formData.lastName}`,
        });
        const newCompany = await addCompany(companyData);
        companyId = newCompany?.id || newCompany;
      }

      // 2. Crear/actualizar contacto tipo COMMERCIAL
      const contactData = {
        contactType: CONTACT_TYPES.COMMERCIAL,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        dni: formData.dni,
        companyName: formData.companyName,
        companyId: companyId,
        address: formData.companyAddress,
        notes: `Contacto creado automáticamente desde comercial: ${formData.firstName} ${formData.lastName}`,
        isActive: formData.isActive,
        updatedAt: new Date().toISOString(),
      };

      if (!contactId) {
        const newContact = await addContact({
          ...contactData,
          createdAt: new Date().toISOString(),
        });
        contactId = newContact?.id || newContact;
      } else {
        await updateContact(contactId, contactData);
      }

      // 3. Crear/actualizar comercial
      const commercialData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dni: formData.dni,
        email: formData.email,
        phone: formData.phone,
        companyName: formData.companyName,
        companyTaxId: formData.companyTaxId,
        companyAddress: formData.companyAddress,
        percentage: formData.percentage,
        contactId: contactId,
        companyId: companyId,
        files: validFiles,
        notes: formData.notes,
        isActive: formData.isActive,
        updatedAt: new Date().toISOString(),
      };

      let savedCommercial;

      if (commercial?.id) {
        await updateCommercial(commercial.id, commercialData);
        savedCommercial = { id: commercial.id, ...commercialData };
      } else {
        const newCommercial = await addCommercial({
          ...commercialData,
          createdAt: new Date().toISOString(),
        });
        const newCommercialId = newCommercial?.id || newCommercial;
        savedCommercial = { id: newCommercialId, ...commercialData };

        // Actualizar el contacto con el commercialId
        if (contactId && newCommercialId) {
          await updateContact(contactId, { commercialId: newCommercialId });
        }
      }

      onSave(savedCommercial);
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.default,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.info.main,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e2e8f0",
        background: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: { xs: 3, sm: 4 },
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}
          >
            {commercial?.id ? "Editar comercial" : "Nuevo comercial"}
          </Typography>
          {!commercial?.id && (
            <Typography
              variant="caption"
              color="info.main"
              fontWeight={500}
              sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
            >
              Se creará automáticamente el contacto y la empresa asociados
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.12)"
                : "#e2e8f0",
          }}
        >
          <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: { xs: 2, sm: 3 },
            borderRadius: "12px",
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
          }}
        >
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Typography sx={sectionTitleSx}>Datos personales</Typography>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, sm: 2.5 },
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Nombre"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
            required
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="Apellidos"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
            required
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="DNI / NIE"
            name="dni"
            value={formData.dni}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
        </Box>

        <Typography sx={sectionTitleSx}>Datos de contacto</Typography>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, sm: 2.5 },
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            size="small"
            type="email"
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="Teléfono"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
        </Box>

        {/* Selector de usuario del sistema */}
        <Autocomplete
          fullWidth
          size="small"
          sx={{ mt: 3 }}
          options={users || []}
          getOptionLabel={(option) =>
            `${option.displayName || option.email} (${option.email})`
          }
          value={users?.find((u) => u.uid === formData.userId) || null}
          onChange={(_, newValue) => {
            setFormData((prev) => ({ ...prev, userId: newValue?.uid || "" }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vincular usuario del sistema"
              placeholder="Seleccionar usuario..."
              sx={fieldSx}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.uid === value.uid}
        />

        <Typography sx={sectionTitleSx}>Empresa</Typography>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, sm: 2.5 },
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Nombre de la empresa"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "300px" } }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="NIF / CIF"
            name="companyTaxId"
            value={formData.companyTaxId}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
          <TextField
            fullWidth
            size="small"
            label="Porcentaje"
            name="percentage"
            value={formData.percentage}
            onChange={handleChange}
            placeholder="Ej: 5%"
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
          />
        </Box>

        <Typography sx={{ ...sectionTitleSx, mt: { xs: 3, sm: 4 } }}>
          Dirección de la empresa
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2.5 },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Calle"
              name="companyAddress.street"
              value={formData.companyAddress.street}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "300px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Ciudad"
              name="companyAddress.city"
              value={formData.companyAddress.city}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "300px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Provincia"
              name="companyAddress.state"
              value={formData.companyAddress.state}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Código postal"
              name="companyAddress.zipCode"
              value={formData.companyAddress.zipCode}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="País"
              name="companyAddress.country"
              value={formData.companyAddress.country}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>
        </Box>

        <Typography sx={sectionTitleSx}>Archivos</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
          }}
        >
          {formData.files.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 1.5 },
                alignItems: "center",
                width: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <TextField
                size="small"
                label="Nombre del archivo"
                value={file.name}
                onChange={(e) =>
                  handleFileChange(index, "name", e.target.value)
                }
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                size="small"
                label="Enlace (URL)"
                value={file.url}
                onChange={(e) => handleFileChange(index, "url", e.target.value)}
                sx={{
                  ...fieldSx,
                  flex: 2,
                  minWidth: { xs: "100%", sm: "300px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <IconButton
                onClick={() => handleRemoveFile(index)}
                size="small"
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: theme.palette.error.main,
                    color: "white",
                  },
                }}
              >
                <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Box>
          ))}

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddFile}
            sx={{
              borderRadius: "12px",
              height: { xs: 40, sm: 44 },
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
              alignSelf: "flex-start",
              mt: 1,
              borderColor: theme.palette.info.main,
              color: theme.palette.info.main,
              "&:hover": {
                borderColor: theme.palette.info.dark,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(2, 136, 209, 0.1)"
                    : "rgba(2, 136, 209, 0.05)",
              },
            }}
          >
            Añadir archivo
          </Button>
        </Box>

        <Typography sx={sectionTitleSx}>Notas internas</Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Notas"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              background: theme.palette.background.paper,
            },
          }}
        />

        <Divider sx={{ mt: { xs: 4, sm: 5 }, mb: { xs: 2, sm: 3 } }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{
              borderRadius: "12px",
              px: { xs: 2, sm: 3 },
              height: { xs: 40, sm: 44 },
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
            }}
            fullWidth={{ xs: true, sm: false }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<Save />}
            sx={{
              borderRadius: "12px",
              px: { xs: 2, sm: 3 },
              height: { xs: 40, sm: 44 },
              boxShadow: "none",
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
              bgcolor: theme.palette.info.main,
            }}
            fullWidth={{ xs: true, sm: false }}
          >
            {loading
              ? "Guardando..."
              : commercial?.id
                ? "Actualizar"
                : "Crear comercial"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default CommercialForm;
